/**
 * Streaming TTS playback for text-first conversations.
 *
 * The legacy /tts Netlify function returns one MP3 URL after the whole utterance is
 * synthesized (2-26s). This module instead consumes the Cloud Run proxy's
 * POST /tts/stream endpoint, which streams NDJSON events carrying base64 PCM16 chunks
 * (~600ms to first audio), and plays them gaplessly through the same Web Audio queue
 * that realtime mode uses (audioUtils).
 *
 * Layering: `readNDJSONStream` is a pure parser with no Web Audio or DOM dependencies,
 * so the wire protocol is testable in isolation; `streamTTSAudio` orchestrates
 * fetch + parse + playback, with the player injectable for tests.
 */
import { _config } from '../Constants';
import { getUrlParameter } from './urlUtils';
import { STOP_PLAYBACK_EVENT } from './playbackControl';
import { isStreamingHealthy, markStreamingUnhealthy } from './streamHealth';
import { createStreamDeadlines } from './streamDeadlines';
import {
  createAudioContext,
  ensureAudioContextRunning,
  decodeAudioToFloat32,
  createAudioBuffer,
  scheduleAudioPlayback,
  stopAllAudio,
} from './audioUtils';

// The server streams 24kHz mono PCM16 (announced again in each stream's meta event).
// The context is created at this rate so buffers play without resampling.
const STREAM_SAMPLE_RATE = 24000;

/**
 * Carries `playedAudio` so the caller can tell a pre-audio failure (safe to fall back
 * to the legacy /tts path) from a mid-playback failure (falling back would replay the
 * utterance from the start, which is worse than silence). `reason` (when set) names
 * a network-shaped failure: first_event_timeout, idle_timeout or network_error.
 */
export class TTSStreamError extends Error {
  constructor(message, { playedAudio = false, reason } = {}) {
    super(message);
    this.name = 'TTSStreamError';
    this.playedAudio = playedAudio;
    if (reason) this.reason = reason;
  }
}

/**
 * Feature flag for the streaming path. URL parameter overrides config so the
 * streaming path can be tested in production before the flag flips.
 * Mirrors the engine-selection pattern in useRealtimeConversation. A network that
 * already failed a stream (streamHealth.js) keeps it off — unless the URL forces it.
 * @returns {boolean}
 */
export function isTTSStreamingEnabled() {
  const urlOverride = getUrlParameter('tts_stream');
  if (urlOverride === 'true') return true;
  if (urlOverride === 'false') return false;
  if (!isStreamingHealthy()) return false;
  return Boolean(_config.ttsStreaming);
}

/**
 * Parse an NDJSON byte stream, invoking `onEvent` once per complete JSON line.
 *
 * Pure protocol layer: no Web Audio, no DOM. Lines may be split across chunks
 * (including mid multi-byte character — TextDecoder in streaming mode buffers those),
 * and a final unterminated line is parsed if non-empty, since some servers omit the
 * trailing newline. A malformed line throws: a corrupt stream must surface, not be
 * silently skipped.
 *
 * @param {{read: () => Promise<{done: boolean, value?: Uint8Array}>}} reader - A ReadableStreamDefaultReader (or compatible stub).
 * @param {(event: object) => void} onEvent - Called with each parsed JSON object.
 */
export async function readNDJSONStream(reader, onEvent) {
  const decoder = new TextDecoder();
  let buffered = '';

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;

    buffered += decoder.decode(value, { stream: true });

    let newlineIndex;
    while ((newlineIndex = buffered.indexOf('\n')) !== -1) {
      const line = buffered.slice(0, newlineIndex).trim();
      buffered = buffered.slice(newlineIndex + 1);
      if (line) {
        onEvent(JSON.parse(line));
      }
    }
  }

  // Flush any bytes the decoder held back (multi-byte char at the stream edge).
  buffered += decoder.decode();
  const trailing = buffered.trim();
  if (trailing) {
    onEvent(JSON.parse(trailing));
  }
}

// --- Module-level player state -------------------------------------------------
// This is not a React component, so the audioUtils "refs" are plain {current} objects.
// One AudioContext for the life of the page: contexts are a limited browser resource,
// and reusing the one unlocked by the user gesture is what keeps iOS playback working.

let streamingAudioContext = null;
const nextStartTimeRef = { current: 0 };
const audioQueueRef = { current: new Set() };
let activeAbortController = null;

function getStreamingAudioContext() {
  if (!streamingAudioContext || streamingAudioContext.state === 'closed') {
    streamingAudioContext = createAudioContext(STREAM_SAMPLE_RATE);
  }
  return streamingAudioContext;
}

/**
 * Production player, built verbatim from the audioUtils primitives realtime mode uses.
 * Kept behind a small interface so tests can inject a fake player and never touch a
 * real AudioContext.
 *
 * Exported so turnStream.js plays through this exact player — the same module-level
 * queue, AudioContext and STOP_PLAYBACK_EVENT listener. The two stream types are
 * mutually exclusive per turn, and sharing one queue keeps "stop everything" a single
 * code path instead of two half-synchronized ones.
 */
// MP3 chunks (the Western-voice turn path sends whole per-sentence MP3 files)
// decode via the async decodeAudioData. Decodes can complete out of order, so
// scheduling is serialized through this chain — scheduleAudioPlayback assigns
// start times in CALL order, and gaplessness depends on that order matching
// arrival order.
let mp3DecodeChain = Promise.resolve();

// Generation token guarding the ASYNC gap between handing a chunk to
// decodeAudioData and scheduling it. stopStreamingPlayback() and each new
// stream bump the generation; a decode that resolves under a stale generation
// is dropped instead of scheduled. Without this, an in-flight decode could
// start playing AFTER a stop event (e.g. the recording-start mic-bleed fix)
// or interleave a dead stream's sentences into the next turn's queue.
let playbackGeneration = 0;

/**
 * Start a fresh playback session: invalidates any in-flight MP3 decodes from a
 * previous stream and resets the decode ordering chain. Called by both stream
 * consumers before their first chunk.
 */
export function beginPlaybackSession() {
  playbackGeneration += 1;
  mp3DecodeChain = Promise.resolve();
}

function base64ToArrayBuffer(base64Audio) {
  const binary = atob(base64Audio);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return bytes.buffer;
}

export function buildDefaultPlayerDeps() {
  return {
    // Throws (pre-audio) if the context cannot be brought to 'running' — e.g. the
    // autoplay policy blocked it because primeStreamingAudio never ran in a gesture.
    // That throw routes the caller to the legacy path, which has its own
    // tap-to-play affordance.
    ensureReady: () => ensureAudioContextRunning(getStreamingAudioContext()),
    beginSession: () => beginPlaybackSession(),
    /**
     * Returns a Promise<boolean>: true once the chunk is actually SCHEDULED for
     * playback, false if it never will be (decode failure, or invalidated by a
     * stop/new stream). Consumers must not claim "audio played" until this
     * resolves true — an all-MP3 decode failure otherwise masquerades as
     * post-audio and suppresses the no-audio fallback.
     */
    playChunk: (base64Audio, sampleRateHertz, onEnded, encoding = 'pcm16') => {
      const ctx = getStreamingAudioContext();
      if (encoding === 'mp3') {
        const generation = playbackGeneration;
        const arrayBuffer = base64ToArrayBuffer(base64Audio);
        const scheduled = mp3DecodeChain
          .then(() => ctx.decodeAudioData(arrayBuffer))
          .then((buffer) => {
            // Stale generation: a stop (or a newer stream) happened while this
            // chunk was decoding. Dropping it here is the entire point of the
            // token — scheduling now would play into whatever came next.
            if (generation !== playbackGeneration) return false;
            scheduleAudioPlayback(ctx, buffer, { nextStartTimeRef, audioQueueRef }, onEnded);
            return true;
          })
          .catch((err) => {
            // A single undecodable sentence shouldn't kill the turn — report the
            // chunk as ended so the done/drain gate can still complete.
            console.warn('[ttsStream] mp3 chunk decode failed:', err?.message);
            if (onEnded) onEnded(audioQueueRef.current.size === 0);
            return false;
          });
        // Keep the chain alive regardless of individual outcomes.
        mp3DecodeChain = scheduled.then(() => undefined, () => undefined);
        return scheduled;
      }
      const float32 = decodeAudioToFloat32(base64Audio);
      const buffer = createAudioBuffer(ctx, float32, sampleRateHertz);
      scheduleAudioPlayback(ctx, buffer, { nextStartTimeRef, audioQueueRef }, onEnded);
      return Promise.resolve(true);
    },
    stop: () => stopAllAudio({ nextStartTimeRef, audioQueueRef }),
  };
}

/**
 * Register the AbortController of whichever NDJSON stream (TTS or conversation turn)
 * is currently in flight. A single module-level slot is the point: the two stream
 * types never run concurrently for one turn, and stopStreamingPlayback() — including
 * the global STOP_PLAYBACK_EVENT path — must be able to kill either from one place.
 */
export function registerActiveStreamAbort(abortController) {
  activeAbortController = abortController;
}

/**
 * Clear the abort slot, but only if it still holds this controller — a newer stream
 * may already have replaced it, and unregistering that one would orphan it.
 */
export function unregisterActiveStreamAbort(abortController) {
  if (activeAbortController === abortController) {
    activeAbortController = null;
  }
}

/**
 * Abort any in-flight stream fetch and silence all queued streamed audio.
 * Safe to call when nothing is playing.
 */
export function stopStreamingPlayback() {
  if (activeAbortController) {
    try {
      activeAbortController.abort();
    } catch (e) {
      // Aborting an already-settled fetch is a no-op we don't care about.
    }
    activeAbortController = null;
  }
  // Invalidate in-flight MP3 decodes BEFORE silencing the queue: a decode that
  // resolves after this stop must be dropped, not scheduled into the silence.
  playbackGeneration += 1;
  stopAllAudio({ nextStartTimeRef, audioQueueRef });
}

/**
 * Recording-watchdog recovery: kill whatever stream is still open when the watchdog
 * releases a stuck microphone, so a late buffered reply can never play over the next
 * turn. The abort surfaces as AbortError, which callers treat as a deliberate stop
 * (the turn is dropped, no legacy re-run). A stream still open by then is the
 * buffering-network signature, so it also turns streaming off for this browser.
 * @returns {boolean} Whether a stream was in flight.
 */
export function releaseStuckStream() {
  const streamWasInFlight = activeAbortController !== null;
  stopStreamingPlayback();
  if (streamWasInFlight) markStreamingUnhealthy('watchdog_stuck_stream');
  return streamWasInFlight;
}

/**
 * Unlock the streaming AudioContext from inside a user gesture.
 *
 * Must be called synchronously in the gesture handler (no await before it): iOS only
 * honours resume() while the gesture is on the stack. Failures are deliberately
 * silent — streamTTSAudio re-checks with ensureAudioContextRunning and throws a
 * pre-audio error if the context still cannot run, which triggers the legacy fallback.
 */
export function primeStreamingAudio() {
  try {
    // Don't route unsupported browsers through createAudioContext here: it alerts,
    // and a priming call is the wrong moment to shout at the user.
    if (typeof window === 'undefined' || !(window.AudioContext || window.webkitAudioContext)) {
      return;
    }
    const ctx = getStreamingAudioContext();
    if (ctx.state !== 'running') {
      const resumed = ctx.resume();
      if (resumed && typeof resumed.catch === 'function') {
        resumed.catch(() => {});
      }
    }
  } catch (e) {
    // Silent by contract — see docblock.
  }
}

/**
 * Stream one utterance from the TTS proxy and play it gaplessly as it arrives.
 *
 * Resolves when the SERVER stream completes (audio may still be draining through the
 * speakers); `callbacks.onPlaybackEnded` fires exactly once when both the stream is
 * done and the playback queue has drained, whichever order those happen in.
 *
 * @param {Object} options
 * @param {string} options.url - Full /tts/stream endpoint URL.
 * @param {Object} options.payload - { text, targetLanguage, userLanguage, speakingRate }.
 * @param {string} [options.idToken] - Firebase ID token, sent as an Authorization
 *   header. Optional today; becomes load-bearing when TTS_STREAM_REQUIRE_AUTH flips
 *   on the proxy.
 * @param {Object} [options.callbacks]
 * @param {Function} [options.callbacks.onFirstAudio] - First audio chunk scheduled.
 * @param {Function} [options.callbacks.onPlaybackEnded] - Stream done AND queue drained.
 * @param {Function} [options.callbacks.onDone] - Server sent its terminal done event.
 * @param {Object} [options.playerDeps] - Test seam: { ensureReady, playChunk, stop }.
 * @returns {Promise<{audioUrl: string|null, playedAudio: boolean}>}
 * @throws {Error} With a `playedAudio` boolean attached — false means no audio reached
 *   the user and the caller may fall back to the legacy path.
 */
export async function streamTTSAudio({ url, payload, idToken, callbacks = {}, playerDeps } = {}) {
  const { onFirstAudio, onPlaybackEnded, onDone } = callbacks;
  const deps = playerDeps || buildDefaultPlayerDeps();

  const abortController = new AbortController();
  registerActiveStreamAbort(abortController);
  const deadlines = createStreamDeadlines({ signal: abortController.signal });

  let playedAudio = false;
  let sampleRateHertz = STREAM_SAMPLE_RATE;
  let doneEvent = null;
  let streamDone = false;
  let queueEmpty = true; // nothing scheduled yet
  let playbackEndedFired = false;

  // onEnded fires per chunk and `done` arrives once; either can complete the pair
  // (done-then-drain or drain-then-done), so both funnel through this single gate.
  const maybeFirePlaybackEnded = () => {
    if (playbackEndedFired || !streamDone || !queueEmpty) return;
    playbackEndedFired = true;
    if (onPlaybackEnded) onPlaybackEnded();
  };

  const handleEvent = (event) => {
    if (!event || typeof event !== 'object') return;

    switch (event.type) {
      case 'meta':
        if (Number.isFinite(event.sampleRateHertz)) {
          sampleRateHertz = event.sampleRateHertz;
        }
        break;

      case 'audio': {
        const isFirst = !playedAudio;
        queueEmpty = false;
        deps.playChunk(event.data, sampleRateHertz, (drained) => {
          queueEmpty = drained;
          maybeFirePlaybackEnded();
        });
        // Only flip after playChunk succeeds: a decode/schedule failure on the very
        // first chunk must still count as "no audio played" so the caller falls back.
        playedAudio = true;
        if (isFirst && onFirstAudio) onFirstAudio();
        break;
      }

      case 'done':
        doneEvent = event;
        streamDone = true;
        // The utterance is complete: a socket that closes late is not a stall to fail on.
        deadlines.stop();
        if (onDone) onDone(event);
        maybeFirePlaybackEnded();
        break;

      case 'error':
        throw new TTSStreamError(event.message || 'TTS stream reported an error', { playedAudio });

      default:
        // Unknown event types are forward-compatible server additions; ignore.
        break;
    }
  };

  try {
    // Pre-audio by construction: any throw here (including a still-suspended context)
    // happens before a single chunk is scheduled, keeping the fallback contract.
    await deps.ensureReady();
    // Invalidate straggler MP3 decodes from any previous turn stream — this
    // stream's PCM chunks must not interleave with a dead stream's sentences.
    if (deps.beginSession) deps.beginSession();

    deadlines.start();
    const response = await deadlines.guard(fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
      },
      body: JSON.stringify(payload),
      signal: abortController.signal,
    }));

    if (!response.ok) {
      throw new TTSStreamError(`TTS stream request failed (HTTP ${response.status})`, { playedAudio: false });
    }
    if (!response.body || typeof response.body.getReader !== 'function') {
      throw new TTSStreamError('Streaming responses not supported in this browser', { playedAudio: false });
    }

    await deadlines.guard(readNDJSONStream(response.body.getReader(), deadlines.wrapHandler(handleEvent)));

    if (!doneEvent) {
      // Connection dropped mid-utterance: the text is incomplete, treat as failure.
      throw new TTSStreamError('TTS stream ended without a done event', { playedAudio });
    }

    return {
      audioUrl: doneEvent.audioUrl != null ? doneEvent.audioUrl : null,
      playedAudio,
    };
  } catch (error) {
    abortController.abort();
    deps.stop();
    // A deadline expiry or network error must never surface as the AbortError our own
    // abort() produces — callers read AbortError as a deliberate stop.
    const networkReason = deadlines.networkFailureReason(error);
    if (networkReason) {
      if (!deadlines.deliveredAnyEvent()) markStreamingUnhealthy(networkReason);
      throw new TTSStreamError(`TTS stream failed (${networkReason}): ${error && error.message}`, { playedAudio, reason: networkReason });
    }
    // Foreign errors (JSON.parse SyntaxError, AbortError) get the playedAudio flag
    // stamped on so the caller has one uniform contract.
    if (error && typeof error === 'object' && typeof error.playedAudio !== 'boolean') {
      try {
        error.playedAudio = playedAudio;
      } catch (stampError) {
        // Frozen error object; the caller will treat missing flag as pre-audio.
      }
    }
    throw error;
  } finally {
    deadlines.stop();
    unregisterActiveStreamAbort(abortController);
  }
}

// Activity switches (and anything else that dispatches the global stop event) must
// kill streamed audio exactly like they kill the <audio> element in
// ConversationComponent. Registered once at module scope.
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener(STOP_PLAYBACK_EVENT, () => stopStreamingPlayback());
}
